import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { MetadataList } from '../../shared/detail/MetadataList';
import type { CommissionLedgerRepository } from './repository';
import type {
  CommissionAdjustmentInput,
  CommissionAdjustmentPreview,
  MockApprovalRequest,
} from './types';
import { compareExactSignedDecimals, formatExactSignedMoney } from './types';

const initialInput: CommissionAdjustmentInput = {
  beneficiaryId: '',
  signedAmount: '',
  currencyCode: '',
  reason: '',
  evidenceReference: '',
};

export function CommissionAdjustmentDialog({
  onClose,
  open,
  repository,
}: {
  readonly onClose: () => void;
  readonly open: boolean;
  readonly repository: CommissionLedgerRepository;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState(initialInput);
  const [preview, setPreview] = useState<CommissionAdjustmentPreview | null>(null);
  const [approval, setApproval] = useState<MockApprovalRequest | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'submit' | null>(null);
  const [error, setError] = useState<AppError | null>(null);
  const submitting = useRef(false);
  const valid = isValid(input);

  const update = (field: keyof CommissionAdjustmentInput, value: string) => {
    setInput((current) => ({
      ...current,
      [field]: field === 'currencyCode' ? value.toUpperCase() : value,
    }));
    setPreview(null);
    setApproval(null);
    setConfirmed(false);
    setError(null);
  };

  const close = () => {
    setInput(initialInput);
    setPreview(null);
    setApproval(null);
    setConfirmed(false);
    setError(null);
    setBusy(null);
    submitting.current = false;
    onClose();
  };

  const previewAdjustment = async () => {
    if (!valid || busy !== null) return;
    setBusy('preview');
    setError(null);
    try {
      setPreview(await repository.previewMockAdjustment(normalize(input)));
    } catch (cause) {
      setError(toSafeAdjustmentError(cause, t('commissionLedger.errors.preview')));
    } finally {
      setBusy(null);
    }
  };

  const submitAdjustment = async () => {
    if (preview === null || !confirmed || submitting.current) return;
    submitting.current = true;
    setBusy('submit');
    setError(null);
    try {
      setApproval(await repository.submitMockAdjustment(normalize(input)));
    } catch (cause) {
      setError(toSafeAdjustmentError(cause, t('commissionLedger.errors.submit')));
      submitting.current = false;
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      aria-describedby="commission-adjustment-description"
      fullWidth
      maxWidth="sm"
      onClose={busy === null ? close : undefined}
      open={open}
    >
      <DialogTitle>{t('commissionLedger.adjustment.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <DataSourceBadge source="mock" />
          <DialogContentText id="commission-adjustment-description">
            {t('commissionLedger.adjustment.description')}
          </DialogContentText>
          {approval === null ? (
            <>
              <TextField
                autoFocus
                label={t('commissionLedger.fields.beneficiaryId')}
                onChange={(event) => update('beneficiaryId', event.target.value)}
                required
                value={input.beneficiaryId}
              />
              <TextField
                error={
                  input.signedAmount.length > 0 && !signedAmountPattern.test(input.signedAmount)
                }
                helperText={t('commissionLedger.adjustment.signedAmountHelp')}
                label={t('commissionLedger.fields.signedAmount')}
                onChange={(event) => update('signedAmount', event.target.value)}
                required
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                value={input.signedAmount}
              />
              <TextField
                label={t('commissionLedger.fields.currency')}
                onChange={(event) => update('currencyCode', event.target.value)}
                required
                slotProps={{ htmlInput: { maxLength: 3 } }}
                value={input.currencyCode}
              />
              <TextField
                label={t('commissionLedger.fields.reason')}
                minRows={2}
                multiline
                onChange={(event) => update('reason', event.target.value)}
                required
                slotProps={{ htmlInput: { maxLength: 2000 } }}
                value={input.reason}
              />
              <TextField
                label={t('commissionLedger.adjustment.evidenceReference')}
                onChange={(event) => update('evidenceReference', event.target.value)}
                required
                value={input.evidenceReference}
              />
              {preview === null ? null : (
                <Paper component="section" variant="outlined" sx={{ p: 2 }}>
                  <Typography component="h3" variant="h6" sx={{ mb: 1 }}>
                    {t('commissionLedger.adjustment.impact')}
                  </Typography>
                  <MetadataList
                    items={[
                      {
                        label: t('commissionLedger.fields.beneficiaryId'),
                        value: preview.beneficiaryId,
                      },
                      {
                        label: t('commissionLedger.fields.signedAmount'),
                        value: formatExactSignedMoney(preview.signedAmount, preview.currencyCode),
                      },
                      {
                        label: t('commissionLedger.adjustment.availableBefore'),
                        value: formatExactSignedMoney(
                          preview.availableBefore,
                          preview.currencyCode,
                        ),
                      },
                      {
                        label: t('commissionLedger.adjustment.availableAfter'),
                        value: formatExactSignedMoney(preview.availableAfter, preview.currencyCode),
                      },
                      { label: t('commissionLedger.fields.reason'), value: preview.reason },
                      {
                        label: t('commissionLedger.adjustment.evidenceReference'),
                        value: preview.evidenceReference,
                      },
                    ]}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmed}
                        onChange={(event) => setConfirmed(event.target.checked)}
                      />
                    }
                    label={t('commissionLedger.adjustment.confirmImpact')}
                  />
                </Paper>
              )}
            </>
          ) : (
            <Alert severity="success">
              {t('commissionLedger.adjustment.approvalCreated', {
                approvalId: approval.approvalId,
              })}
            </Alert>
          )}
          {error === null ? null : (
            <Alert severity="error">
              {error.message}
              {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy !== null} onClick={close}>
          {t('actions.close')}
        </Button>
        {approval !== null ? null : preview === null ? (
          <Button disabled={!valid || busy !== null} onClick={() => void previewAdjustment()}>
            {t('commissionLedger.adjustment.preview')}
          </Button>
        ) : (
          <Button
            disabled={!confirmed || busy !== null}
            onClick={() => void submitAdjustment()}
            variant="contained"
          >
            {t('commissionLedger.adjustment.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

const signedAmountPattern = /^-?(?:0|[1-9][0-9]{0,19})(?:\.[0-9]{1,6})?$/u;
const minimumSignedAdjustment = '-99999999999999999999.999999';
const maximumSignedAdjustment = '99999999999999999999.999999';

export function isValidCommissionAdjustmentAmount(value: string): boolean {
  const amount = value.trim();
  return (
    signedAmountPattern.test(amount) &&
    compareExactSignedDecimals(amount, '0') !== 0 &&
    compareExactSignedDecimals(amount, minimumSignedAdjustment) >= 0 &&
    compareExactSignedDecimals(amount, maximumSignedAdjustment) <= 0
  );
}

function isValid(input: CommissionAdjustmentInput): boolean {
  return (
    input.beneficiaryId.trim().length > 0 &&
    isValidCommissionAdjustmentAmount(input.signedAmount) &&
    /^[A-Za-z]{3}$/u.test(input.currencyCode.trim()) &&
    input.reason.trim().length > 0 &&
    input.evidenceReference.trim().length > 0
  );
}

function normalize(input: CommissionAdjustmentInput): CommissionAdjustmentInput {
  return {
    beneficiaryId: input.beneficiaryId.trim(),
    signedAmount: input.signedAmount.trim(),
    currencyCode: input.currencyCode.trim().toUpperCase(),
    reason: input.reason.trim(),
    evidenceReference: input.evidenceReference.trim(),
  };
}

function toSafeAdjustmentError(cause: unknown, fallback: string): AppError {
  if (isAppError(cause)) return { ...cause, message: fallback };
  return {
    status: null,
    code: 'COMMISSION_ADJUSTMENT_FAILED',
    message: fallback,
    requestId: null,
    fieldErrors: {},
    retryAfterSeconds: null,
  };
}

function isAppError(value: unknown): value is AppError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Partial<AppError>).code === 'string'
  );
}
